import { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { useContracts } from './useContracts';
import { useUserData } from './useUserData';

// The contract now tracks per-type earned/pending natively via
// GRXStorageCore._typedEarned / _typedPending, aggregated by PlanViews.
// getIncomeBreakdown returns:
//   roiEarnedTotal, levelEarnedTotal, rankEarnedTotal, contribSponsorEarnedTotal,
//   roiPending, levelPending, rankPending, contribSponsorPending,
//   contributor10XReleased, contributor10XRemaining
const POLL_MS = 25000;

export const useIncomeBreakdown = () => {
  const { account } = useWallet();
  const c = useContracts();
  const { userId } = useUserData();
  const [state, setState] = useState({
    loading: true,
    roi: 0n, level: 0n, rank: 0n,
    contributor: 0n,          // combined sponsor + own 10X (for compatibility)
    contributorSponsor: 0n,   // income earned from downlines' 10X claims
    contributorPayout: 0n,    // own 10X releases (only present if user IS a contributor)
    total: 0n,
    roiPending: 0n, levelPending: 0n, rankPending: 0n, contribPending: 0n,
    contributor10XReleased: 0n, contributor10XRemaining: 0n,
    error: null,
  });

  useEffect(() => {
    let mounted = true;
    let timer;
    const load = async () => {
      if (!account || !userId || userId === 0n) {
        if (mounted) setState((s) => ({ ...s, loading: false }));
        return;
      }
      try {
        const b = await c.read.views.getIncomeBreakdown(userId);
        if (!mounted) return;
        const roi   = BigInt(b.roiEarnedTotal || 0);
        const lvl   = BigInt(b.levelEarnedTotal || 0);
        const rnk   = BigInt(b.rankEarnedTotal || 0);
        const cSp   = BigInt(b.contribSponsorEarnedTotal || 0);
        const released  = BigInt(b.contributor10XReleased || 0);
        const remaining = BigInt(b.contributor10XRemaining || 0);
        const contributor = cSp + released;
        setState({
          loading: false,
          roi, level: lvl, rank: rnk,
          contributor,
          contributorSponsor: cSp,
          contributorPayout: released,
          total: roi + lvl + rnk + contributor,
          roiPending: BigInt(b.roiPending || 0),
          levelPending: BigInt(b.levelPending || 0),
          rankPending: BigInt(b.rankPending || 0),
          contribPending: BigInt(b.contribSponsorPending || 0),
          contributor10XReleased: released,
          contributor10XRemaining: remaining,
          error: null,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[income-breakdown] view failed:', e?.message || e);
        if (!mounted) return;
        setState((s) => ({ ...s, loading: false, error: e?.message || 'Failed to load' }));
      }
    };
    load();
    timer = setInterval(load, POLL_MS);
    return () => { mounted = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, userId]);

  return state;
};
